import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Users, Loader2, KeyRound, Search, X, ShieldCheck } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null); // user being reset
  const [pass, setPass] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return users;
    const q = query.trim().toLowerCase();
    return users.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q));
  }, [users, query]);

  const openReset = (u) => {
    setTarget(u);
    setPass("");
  };

  const submitReset = async () => {
    if (pass.trim().length < 4) {
      toast.error("Parola nouă trebuie să aibă minim 4 caractere.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/reset-password`, { new_password: pass });
      toast.success(`Parolă resetată pentru ${data.name || data.email}`);
      setTarget(null);
      setPass("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-8 max-w-[1100px] mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Users size={20} />
          <h1 className="text-3xl font-semibold tracking-tight">Utilizatori</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-8">
          Toate conturile din aplicație. Poți reseta parola oricui dacă a uitat-o — îi setezi o parolă nouă
          pe care i-o comunici. Parola veche nu poate fi citită (e criptată).
        </p>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută nume sau email..."
              className="bg-card border border-border rounded-sm pl-9 pr-3 py-1.5 text-sm font-mono outline-none focus:border-primary w-72"
            />
          </div>
          <span className="text-xs text-muted-foreground font-mono ml-auto">{users.length} conturi</span>
        </div>

        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm font-mono">
            <thead className="bg-[#0c0c0e]">
              <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                <th className="px-4 py-3 font-medium">Nume</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Rol</th>
                <th className="px-4 py-3 font-medium">Cargus</th>
                <th className="px-4 py-3 font-medium text-right">Acțiune</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                    <Loader2 size={20} className="mx-auto animate-spin" />
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-border hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">{u.name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-[#4DA3FF]">
                          <ShieldCheck size={13} /> admin
                        </span>
                      ) : (
                        <span className="text-muted-foreground">client</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.has_cargus ? (
                        <span className="text-[#34C759]">configurat</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`reset-pass-${u.id}`}
                        onClick={() => openReset(u)}
                        className="inline-flex items-center gap-1.5 border border-border rounded-sm px-3 py-1.5 text-xs hover:border-primary hover:text-foreground text-muted-foreground transition-colors"
                      >
                        <KeyRound size={13} /> Resetează parola
                      </button>
                    </td>
                  </tr>
                ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                    Niciun utilizator găsit.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="reset-modal">
          <div className="w-full max-w-md border border-border rounded-sm bg-card p-6 relative">
            <button
              onClick={() => setTarget(null)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
              data-testid="reset-close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={18} className="text-primary" />
              <h2 className="text-lg font-semibold">Resetează parola</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Setezi o parolă nouă pentru <span className="text-foreground font-medium">{target.name || target.email}</span>.
              Comunică-i parola nouă, apoi o poate schimba el din contul lui.
            </p>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Parolă nouă</label>
            <input
              data-testid="reset-new-password"
              type="text"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="minim 4 caractere"
              className="mt-1 w-full bg-background border border-border rounded-sm px-2.5 py-2 text-sm font-mono outline-none focus:border-primary"
            />
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setTarget(null)}
                className="flex-1 border border-border rounded-sm px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Anulează
              </button>
              <button
                data-testid="reset-submit"
                disabled={saving}
                onClick={submitReset}
                className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                Salvează parola
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
